import { RETIREMENT_ACCOUNT_TYPES, INDI_JOINT_ACCOUNT_TYPES } from "../constants/accountConstants";

export const MASKED_DOB_DISPLAY = "**-**-****";

export const isMaskedDob = (value) =>
  typeof value === "string" && value.includes("*");


// Kept as a thin alias of `formatDateForDisplay` so existing callers keep
// working; both render MM/DD/YYYY. Use either name in new code — they're
// the same thing.
export const formatDate = (dateStr) => formatDateForDisplay(dateStr);


// Canonical short-date renderer: always MM/DD/YYYY, locale-independent.
// Use this anywhere a date is rendered as plain text in the UI — do NOT
// reach for `toLocaleDateString(...)` (which varies by browser locale and
// is the reason we standardised on this helper).
export const formatDateForDisplay = (dateString) => {
  if (!dateString) return "N/A";
  const str = String(dateString).trim();
  if (!str) return "N/A";
  if (isMaskedDob(str)) return MASKED_DOB_DISPLAY;
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) {
    const [, y, m, d] = ymdMatch;
    return `${m}/${d}/${y}`;
  }
  const mdyMatch = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (mdyMatch) {
    const [, m, d, y] = mdyMatch;
    return `${m.padStart(2, "0")}/${d.padStart(2, "0")}/${y}`;
  }
  const date = new Date(str);
  if (isNaN(date.getTime())) return "N/A";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

export const formatDateForInput = (dateString) => {
  if (!dateString) return "";
  const str = String(dateString).trim();
  if (isMaskedDob(str)) return "";
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch) return str.slice(0, 10);
  const mdyMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    const [, m, d, y] = mdyMatch;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  const date = new Date(str);
  if (isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Compact currency for cards / chips: 1.2M / 500K / 250.
 */
export const formatCompactCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
};

/**
 * "1h ago" / "3d ago" / "just now" — used by the Needs-Attention dashboard
 * Accepts an ISO string or Date.
 */
export const formatRelative = (input) => {
  if (input == null) return "";
  const then = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(then.getTime())) return "";
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 60_000) return "just now";
  const hours = diffMs / (1000 * 60 * 60);
  if (hours < 24) return `${Math.max(1, Math.round(hours))}h ago`;
  const days = hours / 24;
  return `${Math.round(days)}d ago`;
};

export const formatCurrencyPreservingScale = (value) => {
  if (value === null || value === undefined || value === "") return "N/A";

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) return "N/A";

  const decimalPart = String(value).trim().match(/\.(\d+)$/)?.[1];
  const fractionDigits = decimalPart ? Math.min(decimalPart.length, 20) : 0;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(numericValue);
};

export const isJointAccount = (account) => {
  const reg = (account?.registration || "").toLowerCase();
  return reg.includes("joint") || reg.includes("jt");
};

export const isTwoOwnerAccount = (account) =>
  isJointAccount(account) || (account?.registration || "").toUpperCase() === "UTMA/UGMA";

export const isSpecialRegistration = (registration) => {
  const reg = (registration || "").toLowerCase();
  return ["529", "other"].includes(reg);
};

export const isRetirementAccount = (registration) => {
  const reg = (registration || "").toLowerCase();
  return reg.includes("retirement") || reg.includes("ira");
};

/** First names of account owners in owner order (from owners_info + household members). */
export const getAccountOwnerFirstNames = (account, householdMembers = []) => {
  if (!account?.owners_info?.length) return [];
  return account.owners_info
    .map((o) => {
      const m = householdMembers.find((hm) => hm.id === o.id);
      const first = m?.personal_info?.first_name?.trim();
      return first || null;
    })
    .filter(Boolean);
};

/** Single first name, or "First1-First2" for multiple owners. */
export const getAccountOwnerFirstNamesLabel = (account, householdMembers = []) => {
  const firsts = getAccountOwnerFirstNames(account, householdMembers);
  if (firsts.length === 0) return "";
  if (firsts.length === 1) return firsts[0];
  return firsts.join("-");
};

export const formatAccountName = (account, index, householdMembers = []) => {
  if (!account) return 'N/A';

  const accountType = account.type || "N/A";
  const ownerNames = Array.isArray(account.owners_info)
    ? account.owners_info
        .map((owner) => {
          const member = householdMembers.find((m) => m.id === owner.id);
          return member?.personal_info?.first_name;
        })
        .filter(Boolean)
    : [];
  const ownersLabel = ownerNames.length > 0 ? ownerNames.join(" & ") : "N/A";

  return `Account ${index} - ${accountType} - ${ownersLabel}`;
};

/**
 * Human-readable account line for validation or advisor messages (avoids raw account UUIDs).
 */
export function formatAccountContextLabel(
  account,
  householdAccounts = [],
  householdMembers = [],
  householdName = "",
) {
  if (!account) return "This account";
  const idx = householdAccounts.findIndex((a) => a?.id === account.id);
  const index1Based = idx >= 0 ? idx + 1 : 1;
  const title = formatAccountName(account, index1Based, householdMembers);
  const hh = String(householdName || "").trim();
  if (hh) return `${title} — Household: ${hh}`;
  return title;
}

function classifyAccountText(raw) {
  const type = String(raw || "").trim().toLowerCase();
  if (!type) return null;

  // Exact matches from the dropdown
  if (RETIREMENT_ACCOUNT_TYPES.includes(type)) return "retirement";
  if (INDI_JOINT_ACCOUNT_TYPES.includes(type)) return "indi_joint";

  // Real-world variants observed in account summaries/badges (e.g. "Joint/JTWRS", "IRA")
  if (type.includes("joint") || type.includes("jtwros") || type.includes("jt")) return "indi_joint";
  if (type.includes("individual") || type === "indi") return "indi_joint";

  if (type.includes("retirement") || type.includes("ira") || type.includes("roth") || type.includes("sep")) {
    return "retirement";
  }

  if (type.includes("trust")) return "trust";

  return null;
}

/** Classify by account type, falling back to registration when type is blank or unrecognized. */
export function classifyAccount(account) {
  return (
    classifyAccountText(account?.type) ??
    classifyAccountText(account?.registration) ??
    "other"
  );
}

/** Money movements that need manual form fill: withdrawals are auto-filled only for retirement accounts; contributions never are. */
export function getManualFillMoneyMovement(account) {
  const hasContributions =
    Array.isArray(account?.deposits) && account.deposits.length > 0;
  const hasWithdrawals =
    Array.isArray(account?.withdrawals) && account.withdrawals.length > 0;
  const isRetirement = classifyAccount(account) === "retirement";

  return {
    withdrawalUnsupported: hasWithdrawals && !isRetirement,
    contributionUnsupported: hasContributions,
  };
}
